import {HandleProps, HandleType} from 'reactflow';

import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Possible validation states for a node, which change the node's styling.
 */
export enum NodeValidationState {
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

/**
 * Icon type definition for nodes
 */
export type NodeIcon =
  | BenchlingIconName
  | {
      icon: BenchlingIconName;
      color?: string;
    };

/**
 * Handle interaction properties that can be overridden
 */
export type HandleInteractionProps = Partial<
  Pick<HandleProps, 'isConnectable' | 'onConnect' | 'isValidConnection'>
>;

/**
 * Section configuration for a node
 */
export interface NodeSection<TRowData> {
  sectionName: string;
  sectionIcon?: BenchlingIconName;
  sectionRows: Array<TRowData>;
  hideSection?: boolean;
}

/**
 * Core node configuration props
 */
export interface CoreBenchlingNodeData<TRowData> {
  // Required
  nodeName: string;
  sections: Array<NodeSection<TRowData>>;

  // Optional appearance
  nodeIcon?: NodeIcon;
  nodeStatus?: string;
  headerColor?: string;
  nodeSecondaryInfo?: string;
  hideSectionSummaries?: boolean;
  nodeValidationState?: NodeValidationState;
  disabled?: boolean;

  // Optional interactions
  onNodeHeaderClick?: (nodeId: string) => void;
}

/**
 * Props for rendering row handles
 */
export interface RowHandleProps<TRowData> {
  rowData: TRowData;
  handleType: HandleType;
}

/**
 * Props for rendering node handles
 */
export interface NodeHandleProps {
  nodeId: string;
  handleType: HandleType;
}

/**
 * Customization options for the BenchlingNode component
 * @template TRowData - The type of row data for the node
 */
export interface BenchlingNodeCustomization<TRowData> {
  rowRenderer: (rowData: TRowData) => JSX.Element | null;
  /**
   * Render handles in the node header. Returning null will suppress the handle, can be used
   * to conditionally render 0, 1, or both handles for a node.
   */
  nodeHeaderHandleRenderer?: (props: NodeHandleProps) => JSX.Element | null;
}

/**
 * Main node data type to use in ReactFlow that can be extended for custom nodes
 *
 * @example
 * // Custom row data
 * interface MyRowData { id: string; value: number }
 * const nodeData: BenchlingNodeData<MyRowData> = {
 *   nodeName: "Custom Node",
 *   sections: [{ sectionName: "Data", sectionRows: [{ id: "row1", value: 42 }] }],
 *   rowRenderer: (rowData) => <div>{rowData.value}</div>
 * };
 *
 * @example
 * // Custom node handle rendering
 * interface MyRowData { id: string; value: number }
 * const nodeData: BenchlingNodeData<MyRowData> = {
 *   nodeName: "Custom Node",
 *   sections: [{ sectionName: "Data", sectionRows: [{ id: "row1", value: 42 }] }],
 *   rowRenderer: (rowData) => <div>{rowData.value}</div>,
 *   // Only show a source handle for the node header
 *   nodeHeaderHandleRenderer: (props) => props.handleType === HandleType.SOURCE ? <Handle {...props} /> : null,
 * };
 */
export type BenchlingNodeData<TRowData> = CoreBenchlingNodeData<TRowData> &
  BenchlingNodeCustomization<TRowData>;
